import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { noop, omit, throttle, union } from "lodash";
import memoizeOne from "memoize-one";
import * as React from "react";
import { Descendant, Editor, Element, Path, PointRef, Text, Transforms } from "slate";
import { withHistory } from "slate-history";
import { ReactEditor, withReact } from "slate-react";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import { ICannedResponseTag } from "components/message-box/canned-responses";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import MessageBox from "components/message-box/richtext/MessageBox";
import RichEditor from "components/rich-editor";
import {
  hasSignatureInserted,
  isDefinedSignatureEmpty,
  removeSignature,
} from "components/rich-editor/helpers/signature";
import { PluginSpec } from "components/rich-editor/plugins";
import BlockSoftBreaksPlugin from "components/rich-editor/plugins/blockSoftBreaks";
import createCannedResponsesPlugin from "components/rich-editor/plugins/cannedResponses";
import HTMLCopyPastePlugin from "components/rich-editor/plugins/htmlCopyPaste";
import ImagesPlugin from "components/rich-editor/plugins/images";
import LinksPlugin from "components/rich-editor/plugins/links";
import ListBreaksPlugin from "components/rich-editor/plugins/listBreaks";
import buildMentionsPlugin from "components/rich-editor/plugins/mentions";
import OnelineHeadersPlugin from "components/rich-editor/plugins/onelineHeaders";
import RichTextPlugin from "components/rich-editor/plugins/richText";
import SlateCopyPastePlugin from "components/rich-editor/plugins/slateCopyPaste";
import { ReplySuggestions } from "components/rich-editor/reply-suggestions/ReplySuggestions";
import { ReactRichEditor } from "components/rich-editor/types";
import { Shortcuts } from "components/shortcuts";
import { KeyModifier } from "components/shortcuts/Shortcuts";
import { KeyCodeEnum } from "constants/keyCode";
import { EMPTY_EDITOR_STATE } from "constants/rich-text";
import { NEW_TICKET_TEMP_ID, SESSION_STORAGE_DRAFT_KEY } from "constants/tickets/drafts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { CannedResponseInputMethod, ICannedResponseUsage, trackCannedResponseUsage } from "helpers/canneds";
import { isMac } from "helpers/isMac";
import { numberSort } from "helpers/sort";
import { doesMessageIncludeCustomText } from "helpers/tickets";
import { IDraft, TicketStatus } from "interfaces/entities/ticket";
import { ITransactionAttachment } from "interfaces/entities/transaction";
import { getItem } from "services/session-storage";
import { actions$ } from "store/action-stream";
import { RequestAction } from "store/entities/actions";
import { IAgent } from "store/entities/agents";
import { TICKET } from "store/entities/tickets/actions";
import { TRANSACTION } from "store/entities/transaction/actions";
import { TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE } from "store/entities/transaction/constants";
import {
  ITransactionUploadAttachmentFailurePayload,
  ITransactionUploadAttachmentSuccessPayload,
} from "store/entities/transaction/interfaces";
import { ApplicationsActionNames } from "store/features/applications/actions";
import { IHandlePutMessageParams } from "store/features/applications/interfaces";
import { IAction, IActionWithPayload } from "store/helper";
import { withVar } from "themes/helpers";
import { uniqueId } from "utils/string";

import { AttachmentContext } from "./AttachmentProvider";
import TicketAttachments, { IUploadingAttachment } from "./TicketAttachments";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import TicketPrivateMessageSwitch from "./TicketPrivateMessageSwitch";
import { TicketStatusDropdown } from "./TicketStatusDropdown";

interface IProps {
  isSendingMessage: boolean;
  isMutating: boolean;
  agentSignature: IAgent["signature"];
  agentId?: IAgent["id"];
  cannedResponsesByShortcuts: ICannedResponseTag[];
  canSendPublicMessage: boolean;
  canSendPrivateMessage: boolean;
  ticketId?: string;
  ticketStatus?: TicketStatus;
  newTicket?: boolean;
  onMessageSend(
    message: Descendant[],
    isPrivate: boolean,
    status: TicketStatus,
    attachments?: File[],
    transactionID?: string,
    richTextPresent?: boolean,
    mentionedAgents?: string[],
    handleClearMessageBox?: () => void,
    usingShortcut?: boolean,
    cannedResponseIds?: string[],
    replySuggestionIds?: string[]
  ): void;
  onDraftSave?(
    message: Descendant[],
    messageText: string,
    isPrivate: boolean,
    status: TicketStatus,
    cannedResponseIds?: string[],
    replySuggestionIds?: string[]
  ): void;
  transactionID?: string;
  notifyTicketTyping?(ticketId: string): void;
  notifyStopTicketTyping?(ticketId: string): void;
  isMobile?: boolean;
  hasReplySuggestions?: boolean;
}

interface IState {
  value: Descendant[];
  isPrivate: boolean;
  uploadingAttachments: { [localID: string]: IUploadingAttachment & { order: number } };
  ticketStatus: TicketStatus;
  richEditor?: Editor;
  textFormattingVisible: boolean;
  aiTextEnhancementsVisible: boolean;
  cannedResponsePoint: PointRef | null;
  mentionsPoint: PointRef | null;
  cannedResponseIds?: string[];
  replySuggestionIds?: string[];
  // key for resetting reply suggestions' state
  replySuggestionsKey?: string;
}

const Wrapper = styled.div`
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  border-radius: ${(props): string => props.theme.messagebox.border.radius};
  display: flex;
  flex-direction: column;
  gap: ${withVar(SpacingToken.Spacing4)};
`;

const getTextFromEditorState: (editor: Editor, value: Descendant[]) => string = memoizeOne((editor, value) =>
  editor
    ? value.reduce((total, node) => {
        let nodeText = "";
        if (Text.isText(node)) {
          nodeText += node.text;
        }

        if (Element.isElement(node)) {
          if (editor.isVoid(node)) {
            nodeText += "<void>";
          } else {
            nodeText += getTextFromEditorState(editor, node.children);
          }

          if (Editor.isBlock(editor, node)) {
            nodeText += " ";
          }
        }

        return total + nodeText;
      }, "")
    : ""
);

function formIsValid(
  message: string,
  isPrivate: boolean,
  ticketStatus: TicketStatus,
  originalTicketStatus: TicketStatus,
  transactionID?: string,
  attachments?: ITransactionAttachment[]
): boolean {
  const ticketStatusChanged = !isPrivate && originalTicketStatus && ticketStatus !== originalTicketStatus;
  const messageNotEmpty = message.trim().length > 0;
  const isNewTicket = originalTicketStatus === undefined;
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const hasAttachments = transactionID !== null && attachments.length > 0;

  if (isNewTicket) {
    return messageNotEmpty;
  }
  return ticketStatusChanged || messageNotEmpty || hasAttachments;
}

function getInitialState(
  ticketId: string,
  canSendPrivateMessage: boolean,
  canSendPublicMessage: boolean,
  newTicket?: boolean,
  isMobile?: boolean
): IState {
  const ticketDrafts = getItem(SESSION_STORAGE_DRAFT_KEY) || {};
  const draft: IDraft = ticketDrafts[newTicket ? NEW_TICKET_TEMP_ID : ticketId];

  if (draft) {
    const message = typeof draft.message === "string" ? EMPTY_EDITOR_STATE : draft.message;

    let isPrivate: boolean;
    if (!canSendPrivateMessage) {
      isPrivate = false;
    } else if (canSendPublicMessage) {
      // @ts-expect-error TS(2322): Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
      // eslint-disable-next-line prefer-destructuring
      isPrivate = draft.isPrivate;
    } else {
      isPrivate = true;
    }

    return {
      value: message,
      isPrivate,
      uploadingAttachments: {},
      // @ts-expect-error TS(2322): Type 'TicketStatus | undefined' is not assignable ... Remove this comment to see the full error message
      ticketStatus: draft.status,
      textFormattingVisible: !isMobile,
      aiTextEnhancementsVisible: false,
      cannedResponsePoint: null,
      mentionsPoint: null,
      cannedResponseIds: draft.cannedResponseIds,
      replySuggestionIds: draft.replySuggestionIds,
      replySuggestionsKey: uniqueId(),
    };
  }

  return {
    value: EMPTY_EDITOR_STATE,
    isPrivate: canSendPrivateMessage ? !canSendPublicMessage : false,
    uploadingAttachments: {},
    ticketStatus: TicketStatus.Pending,
    textFormattingVisible: !isMobile,
    aiTextEnhancementsVisible: false,
    cannedResponsePoint: null,
    mentionsPoint: null,
    cannedResponseIds: [],
    replySuggestionIds: [uniqueId()],
    replySuggestionsKey: uniqueId(),
  };
}

const ticketMessageSentActions = [TICKET.CREATE[RequestAction.SUCCESS], TICKET.SEND_MESSAGE[RequestAction.SUCCESS]];

export class TicketMessageBox extends React.PureComponent<IProps, IState> {
  static contextType = AttachmentContext;
  // @ts-expect-error TS(2612): Property 'context' will overwrite the base propert... Remove this comment to see the full error message
  context!: React.ContextType<typeof AttachmentContext>;

  static defaultProps = {
    isUnspamming: false,
    onUnspamClick: noop,
    onDraftSave: noop,
    transactionID: null,
    notifyTicketTyping: noop,
    notifyStopTicketTyping: noop,
    newTicket: false,
    agentId: null,
    ticketId: null,
    ticketStatus: null,
    hasReplySuggestions: false,
  };
  state = getInitialState(
    this.props.ticketId ?? "",
    this.props.canSendPrivateMessage,
    this.props.canSendPublicMessage,
    this.props.newTicket,
    this.props.isMobile
  );

  richEditorPlugins: PluginSpec[] = [
    [withHistory, {}],
    ImagesPlugin,
    LinksPlugin,
    SlateCopyPastePlugin,
    HTMLCopyPastePlugin,
    RichTextPlugin,
    ListBreaksPlugin,
    OnelineHeadersPlugin,
    BlockSoftBreaksPlugin,
    createCannedResponsesPlugin({
      onCannedResponseEnter: (pointRef: PointRef, accept) => {
        this.setState({ cannedResponsePoint: pointRef }, () => {
          accept?.();
        });
      },
      onCannedResponseFinish: () => {
        const editor = this.state.richEditor;
        if (editor && !ReactEditor.isFocused(editor)) {
          ReactEditor.focus(editor);
        }
        this.setState({ cannedResponsePoint: null });
      },
    }),
    buildMentionsPlugin(
      (pointRef: PointRef, accept: () => void) => {
        if (this.props.ticketId) {
          this.setState({ mentionsPoint: { ...pointRef } }, () => {
            accept();
          });
        }
      },
      () => {
        const editor = this.state.richEditor;

        if (editor && !ReactEditor.isFocused(editor)) {
          const targetPoint = this.state.mentionsPoint?.current;

          setTimeout(() => {
            ReactEditor.focus(editor);
          });

          if (!targetPoint) return;

          try {
            setTimeout(() => {
              Transforms.select(editor, targetPoint);
              Transforms.move(editor, { distance: 1, unit: "character" });
            });
          } catch {
            setTimeout(() => Transforms.select(editor, Editor.start(editor, [0, 2])));
          }
        }
        this.setState({ mentionsPoint: null });
      }
    ),
    [withReact, {}],
  ];

  orderingCounter = 0;

  handleEditorOnInit = (editor: Editor): void => {
    // eslint-disable-next-line no-param-reassign
    editor.eventPlace = EventPlace.Tickets;
    this.setState({ richEditor: editor });
  };

  handleEditorOnChange = (newValue: Descendant[]): void => {
    this.setState({ value: newValue }, () => {
      this.throttledSaveDraft();
    });
  };

  resetEditorState = (): void => {
    const editor = this.state.richEditor;
    if (!editor) return;

    Editor.withoutNormalizing(editor, () => {
      editor.selection = null;
      this.handleEditorOnChange(EMPTY_EDITOR_STATE);
      this.setState({ textFormattingVisible: true, aiTextEnhancementsVisible: false });
    });
  };

  // @ts-expect-error TS(2564): Property 'unsubscribeMessageSentStream' has no ini... Remove this comment to see the full error message
  private unsubscribeMessageSentStream: () => void; // eslint-disable-line react/sort-comp
  // @ts-expect-error TS(2564): Property 'unsubscribePutMessageStream' has no init... Remove this comment to see the full error message
  private unsubscribePutMessageStream: () => void; // eslint-disable-line react/sort-comp
  // @ts-expect-error TS(2564): Property 'unsubscribeAttachmentChanges' has no ini... Remove this comment to see the full error message
  private unsubscribeAttachmentChanges: () => void; // eslint-disable-line react/sort-comp

  private signatureText = (): string | null => {
    const { agentSignature } = this.props;
    if (agentSignature.richTextObj === null) {
      return agentSignature.text;
    }
    return this.state.richEditor ? getTextFromEditorState(this.state.richEditor, agentSignature.richTextObj) : null;
  };

  private shouldSaveDraft = (): boolean => {
    const { ticketId, newTicket } = this.props;
    const text = this.editorText(this.state.value);
    const signature = this.signatureText();
    return (
      text.trim().length === 0 ||
      // @ts-expect-error TS(2345)
      doesMessageIncludeCustomText(text, signature) ||
      // @ts-expect-error TS(2345)
      (!doesMessageIncludeCustomText(text, signature) &&
        // @ts-expect-error TS(2538): Type 'undefined' cannot be used as an index type.
        !!(getItem(SESSION_STORAGE_DRAFT_KEY) || {})[newTicket ? NEW_TICKET_TEMP_ID : ticketId])
    );
  };

  private throttledSaveDraft = throttle((): void => {
    if (this.shouldSaveDraft()) {
      // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
      this.props.onDraftSave(
        this.state.value,
        this.editorText(this.state.value),
        this.state.isPrivate,
        this.state.ticketStatus,
        this.state.cannedResponseIds,
        this.state.replySuggestionIds
      );
    }
  }, 2000);

  componentDidMount(): void {
    this.unsubscribeMessageSentStream = pipe(
      actions$,
      filter((action: IAction) => ticketMessageSentActions.includes(action.type)),
      subscribe(this.handleMessageSent)
    );

    this.unsubscribePutMessageStream = pipe(
      actions$,
      // @ts-expect-error TS(2345)
      filter((action: IAction) => action.type === ApplicationsActionNames.HANDLE_PUT_MESSAGE),
      subscribe(this.handlePutMessage)
    );

    this.unsubscribeAttachmentChanges = pipe(
      actions$,
      // @ts-expect-error TS(2345)
      filter((action: IAction) =>
        [
          TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.SUCCESS],
          TRANSACTION.UPLOAD_ATTACHMENT[RequestAction.FAILURE],
        ].includes(action.type)
      ),
      subscribe(this.manageAttachmentActions)
    );
  }

  private manageAttachmentActions = (
    action:
      | IActionWithPayload<string, ITransactionUploadAttachmentSuccessPayload>
      | IActionWithPayload<string, ITransactionUploadAttachmentFailurePayload>
  ): void => {
    const { localID } = action.payload;
    this.handleRemoveUpload(localID);
  };

  // @ts-expect-error TS(2345)
  private editorText = (value: Descendant[]): string => getTextFromEditorState(this.state.richEditor, value);

  componentWillUnmount(): void {
    this.unsubscribeMessageSentStream();
    this.unsubscribePutMessageStream();
    this.unsubscribeAttachmentChanges();
  }

  private handleMessageSent = (): void => {
    this.context.reset();
    this.resetEditorState();
  };

  private handlePutMessage = (action: IActionWithPayload<string, IHandlePutMessageParams>): void => {
    const { message } = action.payload;

    if (!this.state.richEditor) {
      return;
    }

    const point = this.state.richEditor.selection || Editor.end(this.state.richEditor, []);

    Transforms.insertText(this.state.richEditor, message, { at: point });
  };

  private handleCannedResponseUse = (id: string, response: string, method: CannedResponseInputMethod): void => {
    this.usedCannedResponses = union(this.usedCannedResponses, [{ response, method }]);
    this.setState(prevState => ({
      cannedResponseIds: union(prevState.cannedResponseIds || [], [id]),
    }));
  };

  private insertTextSignatureToMessage: (agentSignature: string | null) => void = agentSignature => {
    const editor = this.state.richEditor;
    if (!agentSignature || !editor) return;

    const [[lastElement, lastPath]] = Editor.nodes(editor, {
      at: Editor.end(editor, []),
      mode: "lowest",
      match: Element.isElement,
    });

    const lastIsNonFirstEmptyParagraph =
      !Path.equals([0], lastPath) &&
      lastElement.type === "paragraph" &&
      lastElement.children.length === 1 &&
      Text.isText(lastElement.children[0]) &&
      lastElement.children[0].text === "";
    const additionalBreak = lastIsNonFirstEmptyParagraph ? [] : [{ type: "paragraph", children: [{ text: "" }] }];
    const signatureLines = agentSignature
      .split("\n")
      .map(line => ({ type: "paragraph", children: [{ text: line }] })) as Descendant[];

    Transforms.insertFragment(
      editor,
      [
        ...additionalBreak,
        { type: "paragraph", children: [{ text: "" }] },
        {
          type: "paragraph",
          signature: true,
          children: signatureLines,
        },
      ] as Descendant[],
      { at: Editor.end(editor, []) }
    );

    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
    }

    window.setTimeout(() => {
      Transforms.select(editor, Editor.range(editor, Editor.start(editor, []), Editor.start(editor, [])));
    }, 0);
  };

  private insertRichTextSignatureToMessage: (agentSignature: Descendant[]) => void = agentSignature => {
    const editor = this.state.richEditor;
    if (!editor) return;

    const [[lastElement, lastPath]] = Editor.nodes(editor, {
      at: Editor.end(editor, []),
      mode: "lowest",
      match: Element.isElement,
    });
    const { agentId } = this.props;

    const lastIsNonFirstEmptyParagraph =
      !Path.equals([0], lastPath) &&
      lastElement.type === "paragraph" &&
      lastElement.children.length === 1 &&
      Text.isText(lastElement.children[0]) &&
      lastElement.children[0].text === "";
    const additionalBreak = lastIsNonFirstEmptyParagraph ? [] : [{ type: "paragraph", children: [{ text: "" }] }];
    const hasImage = JSON.stringify(agentSignature).indexOf('"type":"image"') !== -1;

    Transforms.insertFragment(
      editor,
      [
        ...additionalBreak,
        { type: "paragraph", children: [{ text: "" }] },
        {
          type: "paragraph",
          signature: true,
          children: agentSignature,
        },
      ] as Descendant[],
      {
        at: Editor.end(editor, []),
      }
    );

    if (hasImage) {
      this.context.mergeTransaction({ agentID: agentId });
    }
    if (!ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
    }

    window.setTimeout(() => {
      Transforms.select(editor, Editor.range(editor, Editor.start(editor, []), Editor.start(editor, [])));
    }, 0);
  };

  private insertSignatureToMessage: () => void = () => {
    if (!this.state.richEditor) return;

    const { agentSignature } = this.props;

    if (isDefinedSignatureEmpty(agentSignature)) return;
    if (hasSignatureInserted(this.state.richEditor)) return;

    if (agentSignature.richTextObj) {
      this.insertRichTextSignatureToMessage(agentSignature.richTextObj);
    } else {
      this.insertTextSignatureToMessage(agentSignature.text);
    }
  };

  private insertSignatureIfNotPrivate = (): void => {
    if (!this.state.richEditor) return;
    if (this.state.isPrivate) return;
    const previousSelection = this.state.richEditor.selection;
    this.insertSignatureToMessage();
    if (previousSelection) {
      Transforms.select(this.state.richEditor, previousSelection);
    }
  };

  private handleModeChange = (event?: React.MouseEvent): void => {
    if (!this.state.richEditor) {
      return;
    }

    event?.stopPropagation();
    event?.preventDefault();

    this.setState(prevState => {
      if (prevState.richEditor) {
        if (!prevState.isPrivate) {
          removeSignature(prevState.richEditor);
        } else if (ReactEditor.isFocused(prevState.richEditor as ReactEditor)) {
          this.insertSignatureToMessage();
        }
      }

      return {
        isPrivate: !prevState.isPrivate,
      };
    });
  };

  private handleStatusChange = (ticketStatus: TicketStatus): void => {
    this.setState({ ticketStatus }, () => {
      this.throttledSaveDraft();
    });
  };

  private handleClearMessageBox = (): void => {
    const editor = this.state.richEditor;
    this.setState({ replySuggestionsKey: uniqueId() });
    if (!editor) return;

    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });
  };

  private handleSave = (usingShortcut?: boolean): void => {
    const { value, isPrivate, ticketStatus, richEditor } = this.state;
    const editorText = this.editorText(this.state.value);
    if (
      formIsValid(
        editorText,
        isPrivate,
        ticketStatus,
        // @ts-expect-error TS(2345): Argument of type 'TicketStatus | undefined' is not... Remove this comment to see the full error message
        this.props.ticketStatus,
        this.props.transactionID,
        this.context.attachments
      )
    ) {
      this.trackCannedResponseUsage();
      this.props.onMessageSend(
        value,
        isPrivate,
        ticketStatus,
        undefined,
        this.props.transactionID,
        editorText.trim().length > 0,
        (this.state.richEditor as ReactRichEditor).mentions.mentionedAgents(),
        this.handleClearMessageBox,
        usingShortcut,
        this.state.cannedResponseIds,
        this.state.replySuggestionIds
      );

      this.setState({ cannedResponseIds: [], replySuggestionIds: [] });
      if (richEditor) {
        ReactEditor.blur(richEditor);
      }
    }
  };

  private handleAttachmentClick = (): void => {
    const { ticketId } = this.props;

    this.context.openFileDialog({
      multiple: true,
      onFileSelect: files => {
        this.setState(prevState => {
          const newAttachments = Array.from(files).reduce((partial, file) => {
            this.orderingCounter += 1;

            const localID = uniqueId();
            // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            // eslint-disable-next-line no-param-reassign
            partial[localID] = { localID, file, order: this.orderingCounter };
            return partial;
          }, {}) as { [localID: string]: IUploadingAttachment & { order: number } };

          Object.values(newAttachments).forEach(attachment => {
            trackEngagement("Editor: Attachment uploaded", EventPlace.Tickets, {
              size: attachment.file.size,
              ticketId,
            });
            this.context.uploadAttachment(attachment.localID, attachment.file, ({ total, loaded }) => {
              this.setState(prevEventState => {
                if (prevEventState.uploadingAttachments[attachment.localID]) {
                  return {
                    uploadingAttachments: {
                      ...prevEventState.uploadingAttachments,
                      [attachment.localID]: {
                        ...prevEventState.uploadingAttachments[attachment.localID],
                        progress: (loaded / total) * 100.0,
                      },
                    },
                  };
                }

                return { uploadingAttachments: prevEventState.uploadingAttachments };
              });
            });
          });

          return { uploadingAttachments: { ...newAttachments, ...prevState.uploadingAttachments } };
        });
      },
    });
  };

  private handleAbortUpload = (localID: string): void => {
    this.context.abortUpload(localID);
    this.handleRemoveUpload(localID);
  };

  private handleRemoveUpload = (localID: string): void => {
    trackEngagement("Editor: Attachment deleted", EventPlace.RichTextEditor);
    this.setState(prevState => {
      const newAttachments = omit(prevState.uploadingAttachments, localID);
      return { uploadingAttachments: newAttachments };
    });
  };

  private trackCannedResponseUsage = (): void => {
    const tracked = trackCannedResponseUsage(
      this.editorText(this.state.value),
      this.usedCannedResponses,
      EventPlace.Tickets
    );
    if (tracked) {
      this.usedCannedResponses = [];
    }
  };

  private handleMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  private setupTypingActivity = (): void => {
    if (this.props.notifyTicketTyping) {
      const { ticketId } = this.props;
      // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
      this.props.notifyTicketTyping(ticketId);
    }
  };

  private disableTypingActivity = (): void => {
    if (this.props.notifyStopTicketTyping) {
      const { ticketId } = this.props;
      // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
      this.props.notifyStopTicketTyping(ticketId);
    }
  };

  private handleMessageBoxFocus = (): void => {
    const { isPrivate } = this.state;
    const { agentSignature } = this.props;
    const editorText = this.editorText(this.state.value);
    this.setupTypingActivity();

    if (
      !isPrivate &&
      agentSignature &&
      (this.signatureText() || "").trim() !== (editorText || "").trim() &&
      // @ts-expect-error TS(2345)
      !doesMessageIncludeCustomText(editorText, this.signatureText())
    ) {
      this.insertSignatureToMessage();
    }
  };

  private handleMessageBoxBlur = (): void => {
    this.disableTypingActivity();
  };

  private handleTextFormattingVisibility = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
    this.setState(prevState => ({
      textFormattingVisible: !prevState.textFormattingVisible,
      aiTextEnhancementsVisible: false,
    }));
  };

  private handleAiTextEnhancementsVisibility = (event: React.MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
    this.setState(prevState => ({
      aiTextEnhancementsVisible: !prevState.aiTextEnhancementsVisible,
      textFormattingVisible: false,
    }));
  };

  // change mode to private only if it's not already private and change to public only if it's not already public and focus editor
  private handleChangeModeAndFocusEditor = (privateMode?: boolean): void => {
    const { isPrivate } = this.state;
    if (privateMode && !isPrivate) {
      this.handleModeChange();
    }
    if (!privateMode && isPrivate) {
      this.handleModeChange();
    }
    // @ts-expect-error TS(2345)
    ReactEditor.focus(this.state.richEditor);
    trackEngagement("Tickets: Shortcut used", EventPlace.Tickets, {
      shortcut: privateMode ? "noteMode" : "replyMode",
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private shortcutsConfig = () => {
    const isMacUser = isMac(navigator.userAgent);
    const { canSendPrivateMessage, canSendPublicMessage } = this.props;

    return {
      [KeyCodeEnum.KeyN]: {
        disableDefaultBehavior: true,
        additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],
        eventHandler: (): void => (canSendPrivateMessage ? this.handleChangeModeAndFocusEditor(true) : noop()),
      },
      [KeyCodeEnum.KeyR]: {
        disableDefaultBehavior: true,
        additionalKeysRequired: [KeyModifier.Alt, KeyModifier.Control],
        eventHandler: canSendPublicMessage ? this.handleChangeModeAndFocusEditor : noop,
      },
      [KeyCodeEnum.Enter]: {
        disableDefaultBehavior: true,
        shouldWorkWhenInputFocused: true,
        additionalKeysRequired: [isMacUser ? KeyModifier.Meta : KeyModifier.Control],
        eventHandler: (): void => this.handleSave(true),
      },
    };
  };

  private handleReplySuggestionUse = (replySuggestionId: string): void => {
    this.setState(prevState => ({
      replySuggestionIds: [...(prevState.replySuggestionIds || []), replySuggestionId],
    }));
  };

  private usedCannedResponses: ICannedResponseUsage[] = [];

  render(): React.ReactNode {
    const {
      cannedResponsesByShortcuts,
      ticketStatus: originalTicketStatus,
      canSendPublicMessage,
      canSendPrivateMessage,
      isSendingMessage,
      hasReplySuggestions,
    } = this.props;
    const {
      value,
      isPrivate,
      ticketStatus,
      textFormattingVisible,
      aiTextEnhancementsVisible,
      uploadingAttachments,
      cannedResponsePoint,
      mentionsPoint,
      replySuggestionsKey,
    } = this.state;
    const isDisabled = !canSendPrivateMessage && !canSendPublicMessage;
    const isInTicket = !!this.props.ticketId;
    return (
      <Wrapper>
        <RichEditor
          value={value}
          onChange={this.handleEditorOnChange}
          onInit={this.handleEditorOnInit}
          plugins={this.richEditorPlugins}
        >
          {isInTicket && hasReplySuggestions && canSendPublicMessage && (
            <ReplySuggestions
              insertSignature={this.insertSignatureIfNotPrivate}
              key={replySuggestionsKey}
              onReplySuggestionUse={this.handleReplySuggestionUse}
            />
          )}
          <MessageBox
            cannedResponsesByShortcuts={cannedResponsesByShortcuts}
            value={value}
            disabled={isDisabled}
            sendButtonLabel="Submit"
            ticketId={this.props.ticketId}
            sendButtonDisabled={
              !formIsValid(
                this.editorText(value),
                isPrivate,
                ticketStatus,
                // @ts-expect-error TS(2345): Argument of type 'TicketStatus | undefined' is not... Remove this comment to see the full error message
                originalTicketStatus,
                this.props.transactionID,
                this.context.attachments
              ) ||
              this.context.isUploading() ||
              this.context.attachmentsSize() > TRANSACTION_ATTACHMENT_MAX_TOTAL_SIZE
            }
            onFocus={this.handleMessageBoxFocus}
            onBlur={this.handleMessageBoxBlur}
            sendButtonLoading={isSendingMessage}
            privateMode={isPrivate}
            maxRows={12}
            placeholder={isPrivate ? "Enter private note visible only to agents" : "Enter message"}
            textFormattingVisible={textFormattingVisible}
            onTextFormattingClick={this.handleTextFormattingVisibility}
            aiTextEnhancementsVisible={aiTextEnhancementsVisible}
            onAiFormattingClick={this.handleAiTextEnhancementsVisibility}
            onModeChange={this.handleModeChange}
            cannedResponsePoint={cannedResponsePoint}
            mentionsPoint={mentionsPoint}
            enableAttachments
            topNodes={
              <TicketAttachments
                uploadingAttachments={Object.values(uploadingAttachments).sort((a1, a2) =>
                  numberSort(a1.order, a2.order)
                )}
                privateMode={isPrivate}
                onAbortUpload={this.handleAbortUpload}
              />
            }
            leftNodes={
              <Shortcuts config={this.shortcutsConfig()}>
                <TicketPrivateMessageSwitch
                  isPrivate={isPrivate}
                  disabled={!canSendPublicMessage}
                  onClick={this.handleModeChange}
                  onMouseDown={this.handleMouseDown}
                />
              </Shortcuts>
            }
            rightNodes={
              originalTicketStatus &&
              canSendPublicMessage &&
              !isPrivate && (
                <TicketStatusDropdown
                  status={ticketStatus}
                  isPrivate={isPrivate}
                  onChange={this.handleStatusChange}
                  onMouseDown={this.handleMouseDown}
                />
              )
            }
            onSendButtonClick={() => this.handleSave()}
            onAttachmentClick={this.handleAttachmentClick}
            onCannedResponseUse={this.handleCannedResponseUse}
            isMutating={false}
          />
        </RichEditor>
      </Wrapper>
    );
  }
}
